import type { Insurance } from "@/types/insurance/insurance";
import { toFixedNumber } from "../utils";
import { getCompensationTaperStartAge } from "./taper-utils";

export function legalBaseLifeInsuranceCompensation(
	age: number,
	maritalStatus: "single" | "married" | "cohabitant" | "partner" | null
): number {
	const MIN_COMPENSATION_PERCENT = 0.2;
	const REDUCTION_RATE = 0.05;
	const BASE_COVERAGE = 15;

	// Spouse supplement
	let sum = 0;

	if (maritalStatus === "single") {
		return 0;
	}

	if (age >= 20 && age <= 69) {
		if (age > 46) {
			sum = BASE_COVERAGE - BASE_COVERAGE * REDUCTION_RATE * (age - 46);
		} else {
			sum = BASE_COVERAGE;
		}

		const minCoverage = BASE_COVERAGE * MIN_COMPENSATION_PERCENT;

		if (sum < minCoverage) {
			sum = minCoverage;
		}
	}

	return toFixedNumber(sum);
}

export function legalChildSupplementLifeInsuranceCompensation(
	childAges: number[],
	soleProvider: boolean
) {
	let sum = 0;
	// Child supplement
	const childAddon: { [key: number]: number } = {
		0: 6.5,
		1: 6.0,
		2: 6.0,
		3: 5.5,
		4: 5.0,
		5: 5.0,
		6: 4.5,
		7: 4.0,
		8: 4.0,
		9: 3.5,
		10: 3.5,
		11: 3.5,
		12: 2.5,
		13: 2.5,
		14: 2.0,
		15: 2.0,
		16: 1.5,
		17: 1.5,
		18: 1.0,
		19: 1.0,
	};

	for (const age of childAges) {
		if (age < 0 || age > 19) {
			continue;
		}

		sum += childAddon[age];
	}

	if (soleProvider) {
		sum = sum * 2;
	}

	return toFixedNumber(sum);
}

export function legalFuneralAllowance(age: number): number {
	const FUNERAL_ALLOWANCE = 0.5;

	if (age <= 72) {
		return FUNERAL_ALLOWANCE;
	}

	return 0;
}

export function legalLifeInsuranceCompensation(
	age: number,
	childAges: number[],
	maritalStatus: "single" | "married" | "cohabitant" | "partner" | null,
	soleProvider: boolean
): number {
	return (
		legalBaseLifeInsuranceCompensation(age, maritalStatus) +
		legalChildSupplementLifeInsuranceCompensation(childAges, soleProvider) +
		legalFuneralAllowance(age)
	);
}

export function leisureAccidentDeathChildSupplement(
	insurance: Insurance | undefined,
	childAges: number[],
	soleProvider: boolean
) {
	let hasChildSupplement: boolean;
	let baseCoverageParam: number;
	let childMaxAge: number;
	let childMinAge: number;

	try {
		hasChildSupplement = insurance?.params?.find(
			(param) => param.type === "child-supplement"
		)?.value as boolean;
		baseCoverageParam = parseFloat(
			insurance?.params?.find(
				(param) => param.type === "base-compensation-child-supplement"
			)?.value as string
		);
		childMaxAge = parseFloat(
			insurance?.params?.find(
				(param) => param.type === "max-age-child-supplement"
			)?.value as string
		);
		childMinAge = parseFloat(
			insurance?.params?.find(
				(param) => param.type === "min-age-child-supplement"
			)?.value as string
		);
	} catch (error) {
		console.error("Error in leisureAccidentDeathChildSupplement:", error);
		return 0;
	}

	if (!childMinAge) {
		childMinAge = 0;
	}

	if (!childMaxAge) {
		childMaxAge = 20;
	}

	let sum = 0;

	if (hasChildSupplement) {
		// Age-based reduction table (same as groupLifeChildSupplement)
		const childAddon: { [key: number]: number } = {
			0: 6.5,
			1: 6.0,
			2: 6.0,
			3: 5.5,
			4: 5.0,
			5: 5.0,
			6: 4.5,
			7: 4.0,
			8: 4.0,
			9: 3.5,
			10: 3.5,
			11: 3.5,
			12: 2.5,
			13: 2.5,
			14: 2.0,
			15: 2.0,
			16: 1.5,
			17: 1.5,
			18: 1.0,
			19: 1.0,
		};

		for (const age of childAges) {
			if (age < childMinAge || age > childMaxAge) {
				continue;
			}

			let childSupplement = 0;

			// If base compensation is set and valid, use it; otherwise use age-based reduction table
			if (baseCoverageParam && !isNaN(baseCoverageParam) && baseCoverageParam > 0) {
				childSupplement = baseCoverageParam;
			} else if (age >= 0 && age < 20) {
				childSupplement = childAddon[age];
			} else if (age >= 20 && age <= childMaxAge) {
				childSupplement = 1;
			}

			if (soleProvider) {
				childSupplement = childSupplement * 2;
			}

			sum += childSupplement;
		}
	}

	return toFixedNumber(sum);
}

export function leisureAccidentDeath(
	insurance: Insurance | undefined,
	age: number,
	childAges: number[],
	maritalStatus: "single" | "married" | "cohabitant" | "partner" | null,
	soleProvider: boolean,
) {
	const MIN_COMPENSATION_PERCENT = 0.2;
	const BASE_COVERAGE = 15;

	let hasDependentsSupplement: boolean;
	let dependentsSupplement: number;
	let hasFixedSpuseSupplement: boolean;
	let spouseMaxAge: number;
	let spouseMinAge: number;
	let childMaxAge: number;
	let childMinAge: number;


	try {
		hasDependentsSupplement = insurance?.params?.find(
			(param) => param.type === "dependents-supplement"
		)?.value as boolean;
		hasFixedSpuseSupplement = insurance?.params?.find(
			(param) => param.type === "fixed-dependents-supplement"
		)?.value as boolean;
		dependentsSupplement = parseFloat(
			insurance?.params?.find(
				(param) => param.type === "base-compensation-dependents-supplement"
			)?.value as string
		);
		spouseMaxAge = parseFloat(
			insurance?.params?.find(
				(param) => param.type === "max-age-dependents-supplement"
			)?.value as string
		);
		spouseMinAge = parseFloat(
			insurance?.params?.find(
				(param) => param.type === "min-age-dependents-supplement"
			)?.value as string
		);
		childMaxAge = parseFloat(
			insurance?.params?.find(
				(param) => param.type === "max-age-child-supplement"
			)?.value as string
		);
		childMinAge = parseFloat(
			insurance?.params?.find(
				(param) => param.type === "min-age-child-supplement"
			)?.value as string
		);
	} catch (error) {
		console.error("Error in leisureAccidentDeath:", error);
		return 0;
	}

	// Use configured reduction rate (stored as %, e.g. "5" → 0.05), fallback to 5%
	const reductionRateRaw = parseFloat(
		insurance?.params?.find(
			(param) => param.type === "dependents-supplement-age-reduction-rate"
		)?.value as string
	);
	const REDUCTION_RATE = !Number.isNaN(reductionRateRaw) && reductionRateRaw > 0
		? reductionRateRaw / 100
		: 0.05;

	if (maritalStatus === "single" && childAges.length === 0) {
		return 0;
	} else if (maritalStatus === "single") {
		let hasChildrenInAgeRange = false;
		for (const age of childAges) {
			if (age >= childMinAge && age <= childMaxAge) {
				hasChildrenInAgeRange = true;
				break;
			}
		}
		if (!hasChildrenInAgeRange) {
			return 0;
		}
	}

	if (!spouseMinAge) {
		spouseMinAge = 0;
	}

	if (!spouseMaxAge) {
		spouseMaxAge = 70;
	}

	if (!childMinAge) {
		childMinAge = 0;
	}

	if (!childMaxAge) {
		childMaxAge = 20;
	}

	if (!dependentsSupplement || dependentsSupplement < BASE_COVERAGE) {
		dependentsSupplement = BASE_COVERAGE;
	}

	let sum = 0;
	if (hasDependentsSupplement) {
		if (hasFixedSpuseSupplement) {
			sum = dependentsSupplement;
		} else if (age >= 20 && age <= 69) {
			const taperStartAge = getCompensationTaperStartAge(insurance);
			if (age > taperStartAge) {
				sum =
					dependentsSupplement -
					dependentsSupplement * REDUCTION_RATE * (age - taperStartAge);
			} else {
				sum = dependentsSupplement;
			}

			const minCoverage = BASE_COVERAGE * MIN_COMPENSATION_PERCENT;

			if (sum < minCoverage) {
				sum = minCoverage;
			}
		}
	}

	// Use the separate child supplement calculation function
	sum += leisureAccidentDeathChildSupplement(insurance, childAges, soleProvider);

	// Add funeral allowance
	sum += leisureAccidentDeathFuneralAllowance(insurance);

	return toFixedNumber(sum);
}

export function leisureAccidentDeathFuneralAllowance(
	insurance: Insurance | undefined
): number {
	const hasFuneralAllowance = insurance?.params?.find(
		(param) => param.type === "funeral-allowance"
	)?.value as boolean;

	if (hasFuneralAllowance) {
		return 0.5;
	}

	return 0;
}

export function leisureAccidentDeathBaseCoverage(
	insurance: Insurance | undefined,
	age: number,
	maritalStatus: "single" | "married" | "cohabitant" | "partner" | null,
	childAges: number[]
) {
	const MIN_COMPENSATION_PERCENT = 0.2;
	const BASE_COVERAGE = 15;

	let hasDependentsSupplement: boolean;
	let dependentsSupplement: number;
	let hasFixedSpuseSupplement: boolean;
	let spouseMaxAge: number;
	let spouseMinAge: number;
	let childMaxAge: number;
	let childMinAge: number;

	try {
		hasDependentsSupplement = insurance?.params?.find(
			(param) => param.type === "dependents-supplement"
		)?.value as boolean;
		hasFixedSpuseSupplement = insurance?.params?.find(
			(param) => param.type === "fixed-dependents-supplement"
		)?.value as boolean;
		dependentsSupplement = parseFloat(
			insurance?.params?.find(
				(param) => param.type === "base-compensation-dependents-supplement"
			)?.value as string
		);
		spouseMaxAge = parseFloat(
			insurance?.params?.find(
				(param) => param.type === "max-age-dependents-supplement"
			)?.value as string
		);
		spouseMinAge = parseFloat(
			insurance?.params?.find(
				(param) => param.type === "min-age-dependents-supplement"
			)?.value as string
		);
		childMaxAge = parseFloat(
			insurance?.params?.find(
				(param) => param.type === "max-age-child-supplement"
			)?.value as string
		);
		childMinAge = parseFloat(
			insurance?.params?.find(
				(param) => param.type === "min-age-child-supplement"
			)?.value as string
		);
	} catch (error) {
		console.error("Error in leisureAccidentDeathBaseCoverage:", error);
		return 0;
	}

	// Use configured reduction rate (stored as %, e.g. "5" → 0.05), fallback to 5%
	const reductionRateRaw = parseFloat(
		insurance?.params?.find(
			(param) => param.type === "dependents-supplement-age-reduction-rate"
		)?.value as string
	);
	const REDUCTION_RATE = !Number.isNaN(reductionRateRaw) && reductionRateRaw > 0
		? reductionRateRaw / 100
		: 0.05;

	if (maritalStatus === "single" && childAges.length === 0) {
		return 0;
	} else if (maritalStatus === "single") {
		let hasChildrenInAgeRange = false;
		for (const age of childAges) {
			if (age >= (childMinAge || 0) && age <= (childMaxAge || 20)) {
				hasChildrenInAgeRange = true;
				break;
			}
		}
		if (!hasChildrenInAgeRange) {
			return 0;
		}
	}

	if (!spouseMinAge) {
		spouseMinAge = 0;
	}

	if (!spouseMaxAge) {
		spouseMaxAge = 70;
	}

	if (!dependentsSupplement || dependentsSupplement < BASE_COVERAGE) {
		dependentsSupplement = BASE_COVERAGE;
	}

	let sum = 0;
	if (hasDependentsSupplement) {
		if (hasFixedSpuseSupplement) {
			sum = dependentsSupplement;
		} else if (age >= 20 && age <= 69) {
			const taperStartAge = getCompensationTaperStartAge(insurance);
			if (age > taperStartAge) {
				sum =
					dependentsSupplement -
					dependentsSupplement * REDUCTION_RATE * (age - taperStartAge);
			} else {
				sum = dependentsSupplement;
			}

			const minCoverage = BASE_COVERAGE * MIN_COMPENSATION_PERCENT;

			if (sum < minCoverage) {
				sum = minCoverage;
			}
		}
	}

	return toFixedNumber(sum);
}

export function funeralAllowance(
	insurance: Insurance | undefined,
	age: number
) {
	const minAgeParam = insurance?.params?.find(
		(param) => param.type === "min-age"
	);
	const maxAgeParam = insurance?.params?.find(
		(param) => param.type === "max-age"
	);

	if (maxAgeParam) {
		if (minAgeParam) {
			if (
				age >= parseFloat(minAgeParam.value as string) &&
				age <= parseFloat(maxAgeParam.value as string)
			) {
				return 0.5;
			}
			return 0;
		}

		if (age <= parseFloat(maxAgeParam.value as string)) {
			return 0.5;
		}
	}

	return 0;
}
